import { useMemo } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useVesselOptions, type VesselOptionDto } from '../api';
import { ComboBox, type ComboOption } from './ComboBox';

export interface VesselSelectProps {
  fiscalYear: number;
  /** 事業実施者で絞り込む。漁協ユーザーは常に自組織。未指定（基金・庁・県）は全事業実施者 */
  businessOperatorId?: number | null;
  /** 期間内に有効な船長・乗組員・乗船人数を持つ船に絞る（dateFrom のみ指定なら 1 日） */
  dateFrom?: string;
  dateTo?: string;
  /** 選択中の参加船 ID（vessels.id） */
  value: number | null;
  /** 選択時。第 2 引数に選択肢の全情報（船長・乗組員・乗船人数・漁業種類など）を渡す */
  onChange: (vesselId: number | null, option?: VesselOptionDto) => void;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  placeholder?: string;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  /** 選択肢に無い船（承認期間外など）が value のときの表示名 */
  fallbackLabel?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

/**
 * 参加船の選択部品（03_作業分担 §6.3）。GET /api/vessels/options（承認済み・期間有効のみ）を使用。
 * 船名・カナ・漁船登録番号・事業実施者名・名簿No で検索できるコンボボックス。
 */
export function VesselSelect({
  fiscalYear,
  businessOperatorId,
  dateFrom,
  dateTo,
  value,
  onChange,
  fallbackLabel,
  placeholder = '船名・カナ・漁船登録番号で検索',
  width = 'md',
  ...rest
}: VesselSelectProps) {
  const { user } = useAuth();
  const own = user?.organizationType === '漁協' && user.businessOperatorId != null ? Number(user.businessOperatorId) : null;
  const opId = own ?? businessOperatorId ?? null;
  const { data, isPending } = useVesselOptions({ fiscalYear, businessOperatorId: opId, dateFrom, dateTo });

  const { options, byValue } = useMemo(() => {
    const list = data ?? [];
    const multiOperator = new Set(list.map((v) => v.businessOperatorId)).size > 1;
    const map = new Map<string, VesselOptionDto>();
    const opts: ComboOption[] = list.map((v) => {
      map.set(String(v.id), v);
      return {
        value: String(v.id),
        label: v.name,
        description: [v.registrationNo, v.nameKana, multiOperator ? v.businessOperatorName : null, `${v.tonnage}トン`].filter(Boolean).join(' ／ '),
        keywords: `${v.nameKana} ${v.registrationNo} ${v.businessOperatorName} ${v.displayOrder} ${v.ownerName}`,
      };
    });
    return { options: opts, byValue: map };
  }, [data]);

  return (
    <ComboBox
      {...rest}
      width={width}
      wideList
      placeholder={placeholder}
      loading={isPending}
      options={options}
      value={value == null ? '' : String(value)}
      fallbackLabel={fallbackLabel ?? (value != null ? `参加船ID ${value}` : undefined)}
      onChange={(v) => (v === '' ? onChange(null) : onChange(Number(v), byValue.get(v)))}
    />
  );
}
